import { FunctionComponent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Story, StoryType } from '../../services';
import {
  Button,
  ButtonType,
  COLLAPSE_STORAGE_KEY,
  CollapseSwitcher,
  DropdownMenu,
  getAppTabActionByStoryTabType,
  Popup,
  SearchField,
  StoryTabType,
  useAppDispatch,
  useAppSelector,
  useAppTabsLabel,
  useDebounce,
} from '../../shared';
import { setStoriesQuery } from '../../slices';
import { CreateStoryModal } from './CreateStoryModal';
import { StoryListAdmin } from './StoryListAdmin';

import dropdownMenuClasses from '../../shared/components/DropdownMenu/DropdownMenu.module.scss';
import classes from './StoryTabListAdmin.module.scss';

interface StoryTabListAdminProps {
  storyTabType?: StoryTabType;
}

export const StoryTabListAdmin: FunctionComponent<StoryTabListAdminProps> = memo(
  ({ storyTabType }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { getAppTabsLabel } = useAppTabsLabel();

    const { query } = useAppSelector(({ stories }) => stories);

    const [createStoryOptionsOpen, setCreateStoryOptionsOpen] = useState<boolean>(false);

    const [createStoryModalOpen, setCreateStoryModalOpen] = useState<boolean>(false);

    const [newStoryType, setNewStoryType] = useState<StoryType>(StoryType.STANDARD);

    const [search, setSearch] = useState(query);

    const createStoryContainerRef = useRef<HTMLDivElement>(null);

    const debouncedSearch = useDebounce(search);

    const createStoryOptionClickHandler = useCallback((storyType: StoryType) => {
      setCreateStoryOptionsOpen(false);
      setCreateStoryModalOpen(true);
      setNewStoryType(storyType);
    }, []);

    const dropdownMenuContent = useMemo(
      () => (
        <>
          {[StoryType.STANDARD, StoryType.READONLY].map((type) => {
            return (
              <button
                key={type}
                type={'button'}
                className={dropdownMenuClasses['dropdown-menu__item']}
                onClick={() => createStoryOptionClickHandler(type)}
              >
                {t(`storyType.${type}`)}
              </button>
            );
          })}
        </>
      ),
      [createStoryOptionClickHandler, t]
    );

    const title = useMemo(() => {
      if (!storyTabType) {
        return t('adminLayoutMenu.mixes');
      }

      return getAppTabsLabel(getAppTabActionByStoryTabType(storyTabType));
    }, [getAppTabsLabel, storyTabType, t]);

    useEffect(() => {
      dispatch(setStoriesQuery(debouncedSearch.trim()));
    }, [debouncedSearch, dispatch]);

    return (
      <>
        <div className={classes['story-tab-list']}>
          <div className={classes['story-tab-list__header']}>
            <h1 className={classes['story-tab-list__title']}>{title}</h1>
            <div className={classes['story-tab-list__buttons']}>
              <CollapseSwitcher storageKey={COLLAPSE_STORAGE_KEY} />

              <div
                ref={createStoryContainerRef}
                className={classes['story-tab-list__create-story']}
              >
                <Button
                  type={ButtonType.primary}
                  label={t('common.create-story')}
                  onClick={() => setCreateStoryOptionsOpen(true)}
                />

                <Popup
                  isOpen={createStoryOptionsOpen}
                  setIsOpen={setCreateStoryOptionsOpen}
                  bodyTop={'0.5rem'}
                  bodyRight={'0'}
                  parentClassName={classes['story-tab-list__create-story-menu']}
                  body={<DropdownMenu width={'10rem'} content={dropdownMenuContent} />}
                />
              </div>
            </div>
          </div>

          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={t('common.type-to-search')}
          />

          <StoryListAdmin storyTabType={storyTabType} />
        </div>

        {createStoryModalOpen && (
          <CreateStoryModal
            isOpen={createStoryModalOpen}
            onClose={() => setCreateStoryModalOpen(false)}
            story={{ type: newStoryType } as Story}
            storyTabType={storyTabType}
          />
        )}
      </>
    );
  }
);
